import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Settings2 } from "lucide-react";
import { toast } from "sonner";

export default function Parametres() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", {
        ...s,
        monthly_goal: Number(s.monthly_goal) || 0,
        urssaf_rate: Number(s.urssaf_rate) || 0,
        tax_rate: Number(s.tax_rate) || 0,
        fuel_price: Number(s.fuel_price) || 0,
        elec_price: Number(s.elec_price) || 0,
      });
      toast.success("Paramètres enregistrés");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Configuration</div>
        <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Paramètres</h1>
      </div>

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Objectifs & taux" icon={Settings2}>
          <FieldRow>
            <Field label="Objectif mensuel (€)"><Input type="number" step="10" value={s.monthly_goal} onChange={(e) => setS({ ...s, monthly_goal: e.target.value })} data-testid="input-monthly-goal" /></Field>
            <Field label="Taux URSSAF (0-1)"><Input type="number" step="0.001" value={s.urssaf_rate} onChange={(e) => setS({ ...s, urssaf_rate: e.target.value })} data-testid="input-urssaf-rate" /></Field>
          </FieldRow>
          <FieldRow>
            <Field label="Taux impôts VLIR (0-1)"><Input type="number" step="0.001" value={s.tax_rate} onChange={(e) => setS({ ...s, tax_rate: e.target.value })} data-testid="input-tax-rate" /></Field>
            <Field label="Prix carburant (€/L)"><Input type="number" step="0.01" value={s.fuel_price} onChange={(e) => setS({ ...s, fuel_price: e.target.value })} data-testid="input-fuel-price" /></Field>
          </FieldRow>
          <FieldRow>
            <Field label="Prix recharge (€/kWh)"><Input type="number" step="0.01" value={s.elec_price} onChange={(e) => setS({ ...s, elec_price: e.target.value })} data-testid="input-elec-price" /></Field>
          </FieldRow>
        </Section>

        <Section title="Informations entreprise" icon={Settings2}>
          <FieldRow>
            <Field label="Nom entreprise"><Input value={s.company_name || ""} onChange={(e) => setS({ ...s, company_name: e.target.value })} data-testid="input-company-name" /></Field>
            <Field label="SIRET"><Input value={s.siret || ""} onChange={(e) => setS({ ...s, siret: e.target.value })} data-testid="input-siret" /></Field>
          </FieldRow>
          <FieldRow>
            <Field label="Adresse"><Input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} data-testid="input-address" /></Field>
            <Field label="Code postal, Ville"><Input value={s.city || ""} onChange={(e) => setS({ ...s, city: e.target.value })} placeholder="69001 Lyon" data-testid="input-city" /></Field>
          </FieldRow>
          <Field label="Téléphone"><Input value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} data-testid="input-phone" /></Field>
        </Section>

        <div className="lg:col-span-2 flex justify-end">
          <Button type="submit" disabled={saving} data-testid="btn-save-settings">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="card-surface p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-manrope font-semibold">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
